import axios from "axios";
import { createContext, FC, useCallback, useContext, useState } from "react";
import { AuthContext } from "../../AuthContext";
import { useLoading } from "../../LoadingContext";
const qs = require("qs");

type AdminListQuery = {
  page: number;
  size: number;
  username: string;
};

type AdminListServiceContextProps = {
  query: AdminListQuery;
  collection: any[];
  balance: number;
  getBalance: (id: string) => Promise<number>;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  roles: { _id: string; roleName: string }[];
  fetchRoles: Function;
  onFetch: Function;
  onFetchV2: (data: any) => Promise<void>;
  setCollection: (collection: any[]) => void;
  updateAdminRole: (id: string, roleId: string) => Promise<void>;
  depositCredit: (id: string, amount: number) => Promise<any>;
  resetAdminOtp: (username: string) => Promise<any>;
  loadUsersOptions: (
    search: string,
    prevOptions: unknown,
    {
      afterCursor,
    }: {
      afterCursor?: {
        _id: string;
      };
    },
  ) => Promise<{
    options: any[];
    hasMore: boolean;
    additional: {
      afterCursor?: {
        _id: string;
      };
    };
  }>;
};

const defaultContext: AdminListServiceContextProps = {
  query: {
    page: 1,
    size: 10,
    username: "",
  },
  collection: [],
  balance: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  roles: [],
  fetchRoles: () => {},
  onFetch: () => {},
  onFetchV2: async () => {},
  setCollection: () => {},
  updateAdminRole: async () => {},
  loadUsersOptions: async (search, prevOptions, { afterCursor }) => ({
    options: [],
    hasMore: false,
    additional: {
      afterCursor: undefined,
    },
  }),
  getBalance: async (username: string) => Promise.resolve(0),
  depositCredit: async (id: string, amount: number) => {},
  resetAdminOtp: async (username: string) => {},
};

const getServiceEndpoint = () => `${process.env.REACT_APP_API_URL}/api/v1/admin/list`;

const getServiceEndpointV2 = () => `${process.env.REACT_APP_API_URL}/api/v2/admin/list`;

const getRolesEndpoint = () => `${process.env.REACT_APP_API_URL}/api/v1/role/list`;

const getAdminsEndpoint = () => `${process.env.REACT_APP_API_URL}/api/v2/admin/list`;

const getCurrentBalanceEndpoint = () => `${process.env.REACT_APP_API_URL}/api/v1/wallet/admin/get`;
const getDepositCreditEndpoint = () =>
  `${process.env.REACT_APP_API_URL}/api/v1/wallet/admin/deposit`;

const resetAdminOtpEndpoint = () => `${process.env.REACT_APP_API_URL}/api/v1/otp/admin/reset`;

export const AdminListServiceContext = createContext(defaultContext);

export const AdminListServiceContextProvider: FC<any> = ({ children }) => {
  const [collection, setCollection] = useState<any[]>([]);
  const [balance, setBalance] = useState<number>(0);
  const [roles, setRoles] = useState<any[]>([]);
  const [page, setPage] = useState<{
    hasNextPage: boolean;
    hasPrevPage: boolean;
  }>({
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [query] = useState<AdminListQuery>(defaultContext.query);
  const authContext = useContext(AuthContext);
  const loading = useLoading();

  // Helper function to fetch admins with V2 API
  const _fetchAdminsV2 = async (queryParams: any, token: string) => {
    const response = await axios.get(getAdminsEndpoint(), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: queryParams,
      paramsSerializer: (params) => {
        return qs.stringify(params, {
          encode: false,
          arrayFormat: "indices",
          indices: true,
          strictNullHandling: true,
        });
      },
    });
    return response;
  };

  const onFetch = useCallback(
    async (data: AdminListQuery) => {
      loading.push();
      try {
        const response = await axios.post(getServiceEndpoint(), data || query, {
          headers: {
            Authorization: `Bearer ${authContext.token}`,
          },
        });

        if (response.data) {
          setCollection(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching collection:", error);
      } finally {
        loading.pop();
      }
    },
    [query, authContext, loading],
  );

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axios.post(
        getRolesEndpoint(),
        {},
        {
          headers: {
            Authorization: `Bearer ${authContext.token}`,
          },
        },
      );
      const roles = response.data.data.map((role: any) => ({
        _id: role._id,
        roleName: role.roleName,
      }));
      setRoles(roles);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  }, [authContext.token]);

  const updateAdminRole = useCallback(
    async (id: string, roleId: string) => {
      try {
        await axios.put(
          `${getServiceEndpoint()}/${id}/role`,
          { roleId },
          {
            headers: {
              Authorization: `Bearer ${authContext.token}`,
            },
          },
        );
      } catch (error) {
        console.error("Error updating admin role:", error);
      }
    },
    [authContext.token],
  );

  const onFetchV2 = useCallback(
    async (data: any) => {
      loading.push();
      try {
        const response = await axios.get(getServiceEndpointV2(), {
          headers: {
            Authorization: `Bearer ${authContext.token}`,
          },
          params: data || query,
          paramsSerializer: (params) => {
            return qs.stringify(params, {
              encode: false,
              arrayFormat: "indices",
              indices: true,
              strictNullHandling: true,
            });
          },
        });
        console.log("Response data:", response.data);
        if (response.data) {
          console.log("Fetched collection v2:", response.data.data.records);
          setCollection(response.data.data.records);
          setPage({
            hasNextPage: response.data.data.hasAfter,
            hasPrevPage: response.data.data.hasBefore,
          });
        }
      } catch (error) {
        console.error("Error fetching v2 collection:", error);
      } finally {
        loading.pop();
      }
    },
    [query, authContext, loading],
  );

  const loadUsersOptions = useCallback(
    async (
      search: string,
      prevOptions: unknown,
      {
        afterCursor,
      }: {
        afterCursor?: {
          _id: string;
        };
      },
    ) => {
      let filteredOptions: any[] = [];
      try {
        const queryParams = {
          filter: {
            username: search !== "" ? search : undefined,
          },
          pagination: {
            afterCursor: afterCursor,
            limit: 10,
            sortParamOrders: [["_id", "asc"]],
          },
          select: {
            // Include fields you want to select
          },
        };

        const response = await _fetchAdminsV2(queryParams, authContext.token);

        if (response && response.data) {
          filteredOptions = response.data.data.records.map((admin: any) => ({
            value: admin._id,
            label: admin.username,
            user: admin,
          }));

          return {
            options: filteredOptions,
            hasMore: response.data.data.hasAfter,
            additional: { afterCursor: response.data.data.afterCursor },
          };
        }
      } catch (error) {
        console.error("Error loading admin options:", error);
      }

      return {
        options: filteredOptions,
        hasMore: false,
        additional: { afterCursor: undefined },
      };
    },
    [authContext.token],
  );

  const getBalance = useCallback(
    async (id: string) => {
      try {
        const response = await axios.get(`${getCurrentBalanceEndpoint()}/${id}`, {
          headers: {
            Authorization: `Bearer ${authContext.token}`,
          },
        });
        setBalance(response.data?.data?.finalBalance || 0);
        return response.data?.data?.finalBalance || 0;
      } catch (error) {
        console.error("Error fetching balance:", error);
        return 0;
      }
    },
    [authContext.token],
  );

  const depositCredit = useCallback(
    async (id: string, amount: number) => {
      try {
        const response = await axios.post(
          getDepositCreditEndpoint(),
          {
            adminId: id,
            balance: amount,
          },
          {
            headers: {
              Authorization: `Bearer ${authContext.token}`,
            },
          },
        );
        console.log("Deposit credit response:", response.data);
        return response.data; // Assuming the API returns some data
      } catch (error) {
        console.error("Error depositing credit:", error);
      }
    },
    [authContext.token],
  );

  const resetAdminOtp = useCallback(
    async (username: string) => {
      try {
        const response = await axios.post(
          resetAdminOtpEndpoint(),
          {
            username,
          },
          {
            headers: {
              Authorization: `Bearer ${authContext.token}`,
            },
          },
        );
        console.log("Reset OTP response:", response.data);
        return response.data; // Assuming the API returns some data
      } catch (error) {
        console.error("Error resetting OTP:", error);
        throw error; // Propagate the error to handle it in the component
      }
    },
    [authContext.token],
  );

  return (
    <AdminListServiceContext.Provider
      value={{
        query,
        collection,
        roles,
        fetchRoles,
        onFetch,
        onFetchV2,
        setCollection,
        updateAdminRole,
        hasNextPage: page.hasNextPage,
        hasPreviousPage: page.hasPrevPage,
        loadUsersOptions,
        getBalance,
        depositCredit,
        resetAdminOtp,
        balance,
      }}
    >
      {children}
    </AdminListServiceContext.Provider>
  );
};
